import type { CSSProperties } from 'react'

/**
 * SPEC-018 A4 — copy tables, type ramp, and error mapping for the cinematic
 * auth threshold screens (ADR-084). All user-facing strings live here so the
 * single access-denied and single auth-error treatments stay purely
 * parameterized by audience + error kind (ported verbatim from the ratified
 * design's auth-copy.jsx).
 */

/** The two role audiences that share the threshold treatments. */
export type AudienceKey = 'admin' | 'consultant'

/** The five recognized auth-error kinds (Screen 4). */
export type ErrorKindKey =
  | 'access_denied'
  | 'consent_required'
  | 'interaction_required'
  | 'login_error'
  | 'default'

interface Audience {
  key: AudienceKey
  label: string
  eyebrow: string
  title: string
  signIn: string
  denied: string
  roleWord: string
}

interface ErrorKind {
  label: string
  eyebrow: string
  title: string
  body: string
  aadsts: string
}

/**
 * Per-audience copy. Admin and consultant differ ONLY in wording; the layout,
 * motion, and structure of every screen are identical.
 */
export const AUDIENCES: Record<AudienceKey, Audience> = {
  admin: {
    key: 'admin',
    label: 'Admin',
    eyebrow: 'Studio Console',
    title: 'Producer or artist access',
    signIn: 'Sign in with your Microsoft account to manage the hub.',
    denied:
      'Your account is signed in, but it hasn’t been granted admin access to the studio console.',
    roleWord: 'admin',
  },
  consultant: {
    key: 'consultant',
    label: 'Consultant',
    eyebrow: 'Shared Workspace',
    title: 'Consultant access',
    signIn: 'Sign in to view the albums shared with you.',
    denied:
      'Your account is signed in, but it hasn’t been granted consultant access to this workspace.',
    roleWord: 'consultant',
  },
}

/** Human copy for each recognized auth-error kind. */
export const ERROR_KINDS: Record<ErrorKindKey, ErrorKind> = {
  access_denied: {
    label: 'access_denied',
    eyebrow: 'Not yet granted',
    title: 'You’re signed in, but not yet cleared',
    body: 'This account hasn’t been granted access. Ask an admin to grant it, then try again.',
    aadsts: 'AADSTS50105',
  },
  consent_required: {
    label: 'consent_required',
    eyebrow: 'Approval needed',
    title: 'This needs an administrator’s approval',
    body: 'An administrator must consent to this app before you can sign in. Reach out to your admin to approve access.',
    aadsts: 'AADSTS65001',
  },
  interaction_required: {
    label: 'interaction_required',
    eyebrow: 'Session ended',
    title: 'Your session has expired',
    body: 'For your security, we ended the session. Please sign in again to continue.',
    aadsts: 'AADSTS50058',
  },
  login_error: {
    label: 'init_error / login_error',
    eyebrow: 'Sign-in interrupted',
    title: 'Something went wrong signing you in',
    body: 'We couldn’t complete sign-in. This is usually temporary — please try again.',
    aadsts: 'AADSTS90099',
  },
  default: {
    label: 'default',
    eyebrow: 'Unexpected',
    title: 'We couldn’t sign you in',
    body: 'An unexpected error occurred. Please try again in a moment.',
    aadsts: 'unknown_error',
  },
}

/**
 * Maps an MSAL/AuthContext error code to one of the recognized error kinds.
 * `init_error` and `login_error` collapse to the single `login_error` copy;
 * anything unrecognized falls back to `default`.
 *
 * @param code - The `error.code` from AuthContext (e.g. 'access_denied', 'login_error').
 * @returns The error-kind key whose copy should render.
 */
export function mapErrorToKind(code: string | undefined): ErrorKindKey {
  switch (code) {
    case 'access_denied':
      return 'access_denied'
    case 'consent_required':
      return 'consent_required'
    case 'interaction_required':
      return 'interaction_required'
    case 'init_error':
    case 'login_error':
      return 'login_error'
    default:
      return 'default'
  }
}

/**
 * Extracts an Entra correlation id from an MSAL error message when present.
 * MSAL surfaces it as `Correlation ID: <uuid>` inside the error description.
 *
 * @param message - The `error.message` from AuthContext, if any.
 * @returns The correlation id, or undefined when none is present.
 */
export function extractCorrelationId(message: string | undefined): string | undefined {
  if (!message) return undefined
  const match = message.match(/correlation\s*id[:\s]+([0-9a-f-]{8,})/i)
  return match ? match[1] : undefined
}

/** Shared type ramp, bound to the scoped `.auth-threshold` design tokens. */
export const T: Record<'eyebrow' | 'display' | 'headline' | 'body', CSSProperties> = {
  eyebrow: {
    fontFamily: 'var(--font-ui)',
    fontSize: '.72rem',
    fontWeight: 600,
    letterSpacing: '.32em',
    textTransform: 'uppercase',
    color: 'var(--gold)',
    margin: 0,
  },
  display: {
    fontFamily: 'var(--font-display)',
    fontSize: 'clamp(3rem, 8vw, 6rem)',
    lineHeight: 1.02,
    fontWeight: 400,
    letterSpacing: '-0.01em',
    color: 'var(--paper)',
    margin: 0,
  },
  headline: {
    fontFamily: 'var(--font-display)',
    fontSize: 'clamp(2rem, 5vw, 4rem)',
    lineHeight: 1.06,
    fontWeight: 400,
    color: 'var(--paper)',
    margin: 0,
  },
  body: {
    fontFamily: 'var(--font-ui)',
    fontSize: 'clamp(1rem, 1.4vw, 1.15rem)',
    lineHeight: 1.6,
    color: 'var(--muted)',
    margin: 0,
    maxWidth: '42ch',
  },
}
